import React from 'react';
import { useListViewLogic } from './hooks/useListViewLogic';
import ListViewHeader from './components/ListViewHeader';
import ListViewTable from './components/ListViewTable';
import BulkActionBar from '../../shared/BulkActionBar';
import './Leader.css';
import './ListView.css';

/**
 * ListView - Die tabellarische Ansicht der Einsätze im Leader Tool.
 * Orchestriert die Header-, Tabellen- und Bulk-Action-Komponenten.
 * 
 * @param {Object} props
 * @param {Array} props.assignments - Die Liste der Einsätze.
 * @param {Array} props.workers - Alle Mitarbeiter für Filterung.
 * @param {Array} props.vehicles - Alle Fahrzeuge für Filterung.
 * @param {Function} props.onAssignmentClick - Klick auf einen Einsatz.
 * @param {Function} props.onAssignmentDelete - Löschen eines Einsatzes.
 * @param {Function} props.onExport - Trigger für PDF Export.
 * @param {Object} props.persistedState - Status für Filter-Persistenz.
 */
export default function ListView({
    assignments,
    workers = [],
    vehicles = [],
    onAssignmentClick,
    onAssignmentHover,
    onAssignmentHoverEnd,
    onAssignmentDelete,
    onNewAssignment,
    onSwitchToCalendar,
    onExport,
    assignmentsForConflicts = [],
    absencesForConflicts = [],
    kolonnen = [],
    isConflictDetectionEnabled = true,
    isReadOnly = false,
    persistedState
}) {
    const {
        searchTerm, setSearchTerm,
        statusFilter, setStatusFilter,
        dateRange, setDateRange,
        dateMode, setDateMode,
        workerFilter, setWorkerFilter,
        vehicleFilter, setVehicleFilter,
        kolonnenFilter, setKolonnenFilter,
        workerSearch, setWorkerSearch,
        vehicleSearch, setVehicleSearch,
        kolonneSearch, setKolonneSearch,
        openDropdown, setOpenDropdown,
        sortConfig, handleSort,
        selectedIds, setSelectedIds,
        toggleSelect, toggleSelectAll,
        hidePast, setHidePast,
        isFilterActive, resetAllFilters,
        sorted,
        STATUS_OPTIONS,
        handleDeleteAssignment,
        handleBulkDelete,
        handleBulkExport,
        handleExport
    } = useListViewLogic({
        assignments,
        workers,
        vehicles,
        kolonnen,
        assignmentsForConflicts,
        absencesForConflicts,
        persistedState,
        onAssignmentDelete,
        onExport
    });

    const filterProps = {
        statusFilter, setStatusFilter,
        dateRange, setDateRange,
        dateMode, setDateMode,
        workerFilter, setWorkerFilter,
        vehicleFilter, setVehicleFilter,
        kolonnenFilter, setKolonnenFilter,
        workerSearch, setWorkerSearch,
        vehicleSearch, setVehicleSearch,
        kolonneSearch, setKolonneSearch,
        openDropdown, setOpenDropdown,
        hidePast, setHidePast,
        isFilterActive, resetAllFilters,
        STATUS_OPTIONS
    };

    return (
        <div className="leader-list-view">
            <ListViewHeader 
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onNewAssignment={onNewAssignment}
                onSwitchToCalendar={onSwitchToCalendar}
                onExport={handleExport}
                isReadOnly={isReadOnly}
            />

            {selectedIds.length > 0 && (
                <div style={{ padding: '0 0 16px 0', boxSizing: 'border-box' }}>
                    <BulkActionBar 
                        selectedCount={selectedIds.length}
                        onClearSelection={() => setSelectedIds([])}
                        actions={[
                            !isReadOnly && {
                                label: `${selectedIds.length} Löschen`,
                                icon: '🗑️',
                                onClick: handleBulkDelete,
                                variant: 'danger'
                            },
                            {
                                label: `${selectedIds.length} Exportieren`,
                                icon: '📄',
                                onClick: handleBulkExport,
                                variant: 'primary'
                            }
                        ].filter(Boolean)}
                    />
                </div>
            )}

            <ListViewTable 
                sorted={sorted}
                sortConfig={sortConfig}
                handleSort={handleSort}
                selectedIds={selectedIds}
                toggleSelect={toggleSelect}
                toggleSelectAll={toggleSelectAll}
                isConflictDetectionEnabled={isConflictDetectionEnabled}
                isReadOnly={isReadOnly}
                assignmentsForConflicts={assignmentsForConflicts}
                absencesForConflicts={absencesForConflicts}
                workers={workers}
                vehicles={vehicles}
                kolonnen={kolonnen}
                onAssignmentClick={onAssignmentClick}
                onAssignmentDelete={handleDeleteAssignment}
                onAssignmentHover={onAssignmentHover}
                onAssignmentHoverEnd={onAssignmentHoverEnd}
                filterProps={filterProps}
            />
        </div>
    );
}
